import React, { useEffect, useState, Fragment, useRef, useMemo } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import { Link } from "react-router-dom";

//MUI
import Typography from "@mui/material/Typography";
import { Button } from "@mui/material";

//Icons
import { ReactComponent as PlusIcon } from "../../assets/icons/svg/plus-solid.svg";

//Infinite scroll
import InfiniteScroll from "react-infinite-scroll-component";

//Components
import Spinner from "../layout/Spinner";
import PostManager from "./PostManager";
import UpdateManager from "../home/UpdateManager";
import FollowSuggestions from "../profile/FollowSuggestions";
import EmptyFeed from "./EmptyFeed";
import EndOfFeed from "./EndOfFeed";
import PlusUpgrade from "./PlusUpgrade";
import PullDownRefresh from "./components/PullDownRefresh";
import ReleaseToRefresh from "./components/ReleaseToRefresh";
import FeedTypeSelector from "./FeedTypeSelector";

//Ads
import InFeedAd from "../ads/feed/InFeedAd";

//Actions
import {
  getFollowingFeed,
  getTrendingFeed,
  refreshFollowingFeed,
  refreshTrendingFeed,
  setFeedType,
} from "../../actions/feed";
import { toggleCreateModal } from "../../actions/navigation";

//Notifications prompt
import { initializeNotifications } from "../../utils/notifications";

//iOS App Tracking Transparency Prompt
import { AppTrackingTransparency } from "capacitor-plugin-app-tracking-transparency";

const FeedManager = ({
  getFollowingFeed,
  getTrendingFeed,
  refreshFollowingFeed,
  refreshTrendingFeed,
  setFeedType,
  feeds,
  toggleCreateModal,
  auth: { isPlus },
}) => {
  const hasInitialized = useRef(false);
  //iOS App Tracking Transparency
  const checkiOSATT = async () => {
    try {
      const status = await AppTrackingTransparency.getStatus();
      console.log("iOS App Tracking Transparency Status:");
      console.log(status?.status);

      if (status && status.status === "notDetermined") {
        const result = await AppTrackingTransparency.requestPermission();
        console.log("iOS App Tracking Transparency Result:");
        console.log(result?.status);
      }
    } catch (error) {
      console.log("iOS App Tracking Transparency Error", error);
    }
  };

  useEffect(() => {
    checkiOSATT();
  }, []);

  useEffect(() => {
    initializeNotifications();
  }, []);

  // Initialize feeds on first load and handle feed type switching
  useEffect(() => {
    if (!feeds?.feedTypeLoading && feeds?.currentFeedType) {
      const currentFeed = feeds?.[feeds?.currentFeedType];

      // Only fetch if feed is empty and not loading
      if (!currentFeed || (!currentFeed.posts && !currentFeed.loading && !currentFeed.error)) {
        if (feeds?.currentFeedType === "following") {
          refreshFollowingFeed();
        } else {
          refreshTrendingFeed();
        }
      }
      hasInitialized.current = true;
    }
  }, [feeds?.currentFeedType, feeds?.feedTypeLoading, feeds, refreshFollowingFeed, refreshTrendingFeed]);

  const currentFeed = feeds?.[feeds?.currentFeedType];

  // Memoize expensive calculations
  const { dataLength, visiblePostIndices } = useMemo(() => {
    if (!currentFeed?.posts) {
      return { dataLength: 0, visiblePostIndices: new Map() };
    }

    const visibleIndices = new Map();
    let visibleCount = 0;

    currentFeed.posts.forEach((post, index) => {
      if (!post.error) {
        visibleIndices.set(index, visibleCount);
        visibleCount++;
      }
    });

    return {
      dataLength: visibleCount,
      visiblePostIndices: visibleIndices,
    };
  }, [currentFeed?.posts]);

  const fetchMoreData = () => {
    if (currentFeed && !currentFeed.loading && !currentFeed.isEndOfFeed) {
      if (feeds?.currentFeedType === "following") {
        getFollowingFeed();
      } else {
        getTrendingFeed();
      }
    }
  };

  const refreshFeed = () => {
    if (feeds?.currentFeedType === "following") {
      refreshFollowingFeed();
    } else {
      refreshTrendingFeed();
    }
  };

  const handleFeedTypeChange = (feedType) => {
    setFeedType(feedType);
  };

  return (
    <div className="w-full flex flex-col items-center justify-center md:pl-[5rem]">
      <UpdateManager />
      <div className="w-full max-w-[768px] md:max-w-[600px] 3xl:max-w-[768px] flex flex-col items-center [&_>_div:first-child]:w-full [&_>_div:first-child]:overflow-x-hidden safe-nav-padding [&_>_div]:w-full ">
        <div>
          <FeedTypeSelector />
        </div>
        <div className="flex flex-col-reverse sm:flex-row justify-between md:rounded-lg border-y md:border border-separator md:shadow-sm bg-background relative z-10">
          <div className="w-full pb-4 px-4 pt-2 sm:p-4 sm:w-1/2 ">
            <Link to="/create/post">
              <Button
                disableRipple
                className="p-0 flex flex-col items-start justify-start h-full hover:bg-transparent text-start"
              >
                <div className="flex flex-row gap-2 items-center">
                  <PlusIcon className="h-[0.75rem] fill-mainGreen" />
                  <Typography
                    variant="subtitle1"
                    className="font-normal text-primaryText leading-snug text-sm md:text-inherit"
                  >
                    Create Post
                  </Typography>
                </div>

                <Typography variant="caption" className="text-secondaryText inline-block">
                  Share your culinary creations
                </Typography>
              </Button>
            </Link>
          </div>

          <div className="w-full border-t sm:w-1/2 sm:border-t-0 sm:border-l border-separator pt-4 px-4 pb-2 sm:p-4">
            <Button
              onClick={() => toggleCreateModal(true)}
              disableRipple
              className="p-0 flex flex-col items-start justify-start h-full hover:bg-transparent text-start"
            >
              <div className="flex flex-row gap-2 items-center">
                <PlusIcon className="h-[0.75rem] fill-mainGreen" />
                <Typography
                  variant="subtitle1"
                  className="font-normal text-primaryText leading-snug text-sm md:text-inherit"
                >
                  Add Recipe
                </Typography>
              </div>

              <Typography variant="caption" className="text-secondaryText inline-block">
                Create, Import, or Generate a Recipe
              </Typography>
            </Button>
          </div>
        </div>

        {feeds?.feedTypeLoading ? (
          <Spinner classNameOverride="mt-8" />
        ) : !currentFeed ? (
          <Spinner classNameOverride="mt-8" />
        ) : currentFeed.loading ? (
          <Spinner classNameOverride="mt-8" />
        ) : currentFeed.posts && currentFeed.posts.length > 0 ? (
          <InfiniteScroll
            dataLength={dataLength}
            next={fetchMoreData}
            hasMore={!currentFeed.isEndOfFeed && !currentFeed.loading && dataLength > 0}
            loader={currentFeed.loading && <Spinner classNameOverride="mt-8" />}
            pullDownToRefresh
            refreshFunction={refreshFeed}
            pullDownToRefreshThreshold={75}
            pullDownToRefreshContent={<PullDownRefresh />}
            releaseToRefreshContent={<ReleaseToRefresh />}
            endMessage={<EndOfFeed />}
            className="h-full w-full [&_>_div]:md:mb-4"
          >
            {currentFeed.posts &&
              currentFeed.posts.map((post, i) => {
                const visiblePostIndex = visiblePostIndices.get(i) ?? -1;
                const postId = post.postid || post.id || post._id || `post-${i}`;
                const uniqueKey = `${feeds.currentFeedType}-${postId}-${i}`;

                // Only render PostManager for non-error posts
                if (post.error) {
                  return null;
                }

                return (
                  <Fragment key={uniqueKey}>
                    <PostManager index={i} />
                    {(visiblePostIndex === 5 || (visiblePostIndex > 5 && (visiblePostIndex - 5) % 25 === 0)) &&
                      visiblePostIndex !== dataLength - 1 && (
                        <FollowSuggestions key={`profile-suggestions-${uniqueKey}`} />
                      )}
                    {!isPlus &&
                      (visiblePostIndex === 7 || (visiblePostIndex > 7 && (visiblePostIndex - 7) % 20 === 0)) && (
                        <PlusUpgrade key={`plus-upgrade-${uniqueKey}`} />
                      )}
                  </Fragment>
                );
              })}
          </InfiniteScroll>
        ) : (
          <EmptyFeed />
        )}
      </div>
    </div>
  );
};

FeedManager.propTypes = {
  getFollowingFeed: PropTypes.func.isRequired,
  getTrendingFeed: PropTypes.func.isRequired,
  refreshFollowingFeed: PropTypes.func.isRequired,
  refreshTrendingFeed: PropTypes.func.isRequired,
  setFeedType: PropTypes.func.isRequired,
  feeds: PropTypes.object.isRequired,
  toggleCreateModal: PropTypes.func.isRequired,
  auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  feeds: state.feed.feeds,
  auth: state.auth,
});

export default connect(mapStateToProps, {
  getFollowingFeed,
  getTrendingFeed,
  refreshFollowingFeed,
  refreshTrendingFeed,
  setFeedType,
  toggleCreateModal,
})(FeedManager);
